import { useState, useEffect } from 'react';
import { Typography, Stack, Alert, Button, TextField, Box, IconButton, InputAdornment } from '@mui/material';
import { Link as RouterLink, useParams, useNavigate } from 'react-router-dom';
import { ArrowBack, CheckCircle, Visibility, VisibilityOff } from '@mui/icons-material';
import AuthLayout from '../components/auth/AuthLayout';
import { useLang } from '../contexts/langContext';
import { useBranding } from '../contexts/BrandingContext';
import api from '../services/api';
import { getStrongPasswordErrorMessage, isStrongPassword } from '../utils/password';

function ResetPasswordPage() {
  const { lang } = useLang();
  const { requireStrongPassword } = useBranding();
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const isEnglish = lang === 'en';

  useEffect(() => {
    if (!token) {
      navigate('/login');
    }
  }, [token, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    setMessage('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setError(isEnglish ? 'Passwords do not match' : '密码不匹配');
      setIsLoading(false);
      return;
    }

    if (requireStrongPassword) {
      if (!isStrongPassword(password)) {
        setError(getStrongPasswordErrorMessage(lang));
        setIsLoading(false);
        return;
      }
    } else if (password.length < 6) {
      setError(isEnglish ? 'Password must be at least 6 characters' : '密码至少需要6个字符');
      setIsLoading(false);
      return;
    }

    try {
      const response = await api({ feedbackMode: 'off' }).post('/auth/reset-password', {
        token,
        password,
        lang,
      });

      setMessage(response.data.message);
      setIsSuccess(true);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err: any) {
      const errorMessage = err.response?.data?.error || 
        (isEnglish ? 'An error occurred. Please try again.' : '发生错误，请重试。');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <AuthLayout title={isEnglish ? 'Set New Password' : '设置新密码'}>
        <Alert 
          severity="success" 
          variant="outlined"
          icon={<CheckCircle />}
          sx={{ mb: 3 }}
        >
          {message}
        </Alert>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
          {isEnglish 
            ? 'Redirecting to login page in 3 seconds...' 
            : '3秒后跳转到登录页面...'
          }
        </Typography>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={isEnglish ? 'Set New Password' : '设置新密码'}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
        {isEnglish 
          ? "Please enter your new password" 
          : "请输入您的新密码"
        }
      </Typography>

      <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            type={showPassword ? 'text' : 'password'}
            label={isEnglish ? 'New Password' : '新密码'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={isEnglish ? 'Enter your new password' : '请输入新密码'}
            required
            disabled={isLoading}
            inputProps={{ minLength: requireStrongPassword ? 8 : 6 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <TextField
            fullWidth
            type={showConfirmPassword ? 'text' : 'password'}
            label={isEnglish ? 'Confirm New Password' : '确认新密码'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={isEnglish ? 'Confirm your new password' : '请确认新密码'}
            required
            disabled={isLoading}
            inputProps={{ minLength: requireStrongPassword ? 8 : 6 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={isLoading}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error && (
            <Alert severity="error" variant="outlined">
              {error}
            </Alert>
          )}

          <Button 
            type="submit" 
            fullWidth 
            variant="contained" 
            size="large"
            disabled={isLoading || !password.trim() || !confirmPassword.trim()}
            sx={{ py: 1.5 }}
          >
            {isLoading ? (
              isEnglish ? 'Resetting...' : '重置中...'
            ) : (
              isEnglish ? 'Reset Password' : '重置密码'
            )}
          </Button>
        </Stack>
      </Box>
      
      <Stack sx={{ textAlign: 'center' }}>
        <Button
          component={RouterLink}
          to="/login"
          startIcon={<ArrowBack />}
          variant="text"
          sx={{ 
            color: 'text.secondary',
            '&:hover': {
              color: 'primary.main',
            }
          }}
        >
          {isEnglish ? 'Back to login' : '返回登录'}
        </Button>
      </Stack>
    </AuthLayout>
  );
}

export default ResetPasswordPage; 